import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RFXRequestAuditRecord } from '../../interfaces/RFXAuditRecordModel';
import { Notification } from '../../interfaces/RFXBackendModel';
import { 
  InsertRFXQuestionDocumentEntryInput,
  RFXQuestion, 
  RFXQuestionDocumentEntry} from '../../interfaces/RFXQuestionModel';
import { BUYER_VIEW_RESP_MODEL } from '../../resources/RFXResponseTable-data';
import { RootState } from '../../store';

export interface BuyerViewPageState {
  isLoading: boolean;
  isRespTableLoading: boolean;
  isBidAwarded: boolean;
  isBidSoftAwarded: boolean;
  respTableData: BUYER_VIEW_RESP_MODEL[];
  requestId: { id: string; revision: number };
  requestDueAt: number;
  requestStatus: string;
  requestBusiness: string;
  responseIdForOffer: string;
  currentRound: number;
  supplierNameMap: Map<string, string>;
  selectedSupplier: string;
  isQuestionLoading: boolean;
  userToQuestionMap: Map<string, RFXQuestion[]>;
  userToQuestionDocumentMap: { [userId: string]: RFXQuestionDocumentEntry[] };
  isSplitPanelOpen: boolean;
  splitPanelActiveTabId: string;
  offerIds: string[];
  isAuditRecordsLoading: boolean;
  auditRecords: RFXRequestAuditRecord[];
  notifications: Notification[];
  unreadNotificationBadge: number;
}

const initialState: BuyerViewPageState = {
  isLoading: true,
  isRespTableLoading: true,
  isBidAwarded: false,
  isBidSoftAwarded: false,
  respTableData: [],
  requestId: {
    id: '',
    revision: 0
  },
  requestDueAt: 0,
  requestStatus: '',
  requestBusiness: '',
  responseIdForOffer: '',
  currentRound: 0,
  supplierNameMap: new Map<string, string>(),
  selectedSupplier: '',
  isQuestionLoading: true,
  userToQuestionMap: new Map<string, RFXQuestion[]>(),
  userToQuestionDocumentMap: {},
  isSplitPanelOpen: false,
  splitPanelActiveTabId: 'conversation',
  offerIds: [],
  isAuditRecordsLoading: true,
  auditRecords: [],
  notifications: [],
  unreadNotificationBadge: 0
};

const buyerViewPageSlice = createSlice({
  name: 'buyerViewPage',
  initialState,
  reducers: {
    setIsLoading(state: BuyerViewPageState, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setIsRespTableLoading(state: BuyerViewPageState, action: PayloadAction<boolean>) {
      state.isRespTableLoading = action.payload;
    },
    setIsBidAwarded(state: BuyerViewPageState, action: PayloadAction<boolean>) {
      state.isBidAwarded = action.payload;
    },
    setIsBidSoftAwarded(state: BuyerViewPageState, action: PayloadAction<boolean>) {
      state.isBidSoftAwarded = action.payload;
    },
    setRespTableData(state: BuyerViewPageState, action: PayloadAction<BUYER_VIEW_RESP_MODEL[]>) {
      state.respTableData = action.payload;
    },
    setRequestId(state: BuyerViewPageState, action: PayloadAction<{ id: string; revision: number }>) {
      state.requestId = action.payload;
    },
    setRequestDueAt(state: BuyerViewPageState, action: PayloadAction<number>) {
      state.requestDueAt = action.payload;
    },
    setRequestStatus(state: BuyerViewPageState, action: PayloadAction<string>) {
      state.requestStatus = action.payload;
    },
    setRequestBusiness(state: BuyerViewPageState, action: PayloadAction<string>) {
      state.requestBusiness = action.payload;
    },
    setResponseIdForOffer(state: BuyerViewPageState, action: PayloadAction<string>) {
      state.responseIdForOffer = action.payload;
    },
    setCurrentRound(state: BuyerViewPageState, action: PayloadAction<number>) {
      state.currentRound = action.payload;
    },
    setSupplierNameMap(state: BuyerViewPageState, action: PayloadAction<Map<string, string>>) {
      state.supplierNameMap = action.payload;
    },
    setSelectedSupplier(state: BuyerViewPageState, action: PayloadAction<string>) {
      state.selectedSupplier = action.payload;
    },
    setIsQuestionLoading(state: BuyerViewPageState, action: PayloadAction<boolean>) {
      state.isQuestionLoading = action.payload;
    },
    setUserToQuestionMap(state: BuyerViewPageState, action: PayloadAction<Map<string, RFXQuestion[]>>) {
      state.userToQuestionMap = action.payload;
    },
    setUserToQuestionDocumentMap(
        state: BuyerViewPageState,
        action: PayloadAction<{ [userId: string]: RFXQuestionDocumentEntry[] }>) 
    {
      state.userToQuestionDocumentMap = action.payload;
    },
    insertEntryIntoUserToQuestionDocumentMap(
      state: BuyerViewPageState,
      action: PayloadAction<InsertRFXQuestionDocumentEntryInput>) 
    {
      const userId = action.payload.userId;
      let entriesToUpdate = state.userToQuestionDocumentMap[userId] ? 
        [action.payload.documentEntry, ...state.userToQuestionDocumentMap[userId]] : [action.payload.documentEntry];
      state.userToQuestionDocumentMap[userId] = entriesToUpdate;
    },
    setIsSplitPanelOpen(state: BuyerViewPageState, action: PayloadAction<boolean>) {
      state.isSplitPanelOpen = action.payload;
    },
    setSplitPanelActiveTabId(state: BuyerViewPageState, action: PayloadAction<string>) {
      state.splitPanelActiveTabId = action.payload;
    },
    setOfferIds(state: BuyerViewPageState, action: PayloadAction<string[]>) {
      state.offerIds = action.payload;
    },
    setIsAuditRecordsLoading(state: BuyerViewPageState, action: PayloadAction<boolean>) {
      state.isAuditRecordsLoading = action.payload;
    },
    setAuditRecords(state: BuyerViewPageState, action: PayloadAction<RFXRequestAuditRecord[]>) {
      state.auditRecords = action.payload;
    },
    setNotifications(state: BuyerViewPageState, action: PayloadAction<Notification[]>) {
      state.notifications = action.payload;
    },
    setUnreadNotificationBadge(state: BuyerViewPageState, action: PayloadAction<number>) {
      state.unreadNotificationBadge = action.payload;
    }
  }
});

export const {
  setIsLoading,
  setIsRespTableLoading,
  setIsBidAwarded,
  setIsBidSoftAwarded,
  setRespTableData,
  setRequestId,
  setRequestDueAt,
  setRequestStatus,
  setRequestBusiness,
  setResponseIdForOffer,
  setCurrentRound,
  setSupplierNameMap,
  setSelectedSupplier,
  setIsQuestionLoading,
  setUserToQuestionMap,
  setUserToQuestionDocumentMap,
  setIsSplitPanelOpen,
  setSplitPanelActiveTabId,
  setOfferIds,
  setIsAuditRecordsLoading,
  setAuditRecords,
  insertEntryIntoUserToQuestionDocumentMap,
  setNotifications,
  setUnreadNotificationBadge
} = buyerViewPageSlice.actions;

export const selectIsLoading = (state: RootState) => state.buyerViewPage.isLoading;

export const selectIsRespTableLoading = (state: RootState) => state.buyerViewPage.isRespTableLoading;

export const selectIsBidAwarded = (state: RootState) => state.buyerViewPage.isBidAwarded;

export const selectIsBidSoftAwarded = (state: RootState) => state.buyerViewPage.isBidSoftAwarded;

export const selectRespTableData = (state: RootState) => state.buyerViewPage.respTableData;

export const selectRequestId = (state: RootState) => state.buyerViewPage.requestId;

export const selectRequestDueAt = (state: RootState) => state.buyerViewPage.requestDueAt;

export const selectRequestStatus = (state: RootState) => state.buyerViewPage.requestStatus;

export const selectRequestBusiness = (state: RootState) => state.buyerViewPage.requestBusiness;

export const selectResponseIdForOffer = (state: RootState) => state.buyerViewPage.responseIdForOffer;

export const selectCurrentRound = (state: RootState) => state.buyerViewPage.currentRound;

export const selectSupplierNameMap = (state: RootState) => state.buyerViewPage.supplierNameMap;

export const selectSelectedSupplier = (state: RootState) => state.buyerViewPage.selectedSupplier;

export const selectIsQuestionLoading = (state: RootState) => state.buyerViewPage.isQuestionLoading;

export const selectUserToQuestionMap = (state: RootState) => state.buyerViewPage.userToQuestionMap;

export const selectUserToQuestionDocumentMap = (state: RootState) => state.buyerViewPage.userToQuestionDocumentMap;

export const selectIsSplitPanelOpen = (state: RootState) => state.buyerViewPage.isSplitPanelOpen;

export const selectSplitPanelActiveTabId = (state: RootState) => state.buyerViewPage.splitPanelActiveTabId;

export const selectOfferIds = (state: RootState) => state.buyerViewPage.offerIds;

export const selectIsAuditRecordsLoading = (state: RootState) => state.buyerViewPage.isAuditRecordsLoading;

export const selectAuditRecords = (state: RootState) => state.buyerViewPage.auditRecords;

export const selectNotifications = (state: RootState) => state.buyerViewPage.notifications;

export const selectUnreadNotificationBadge = (state: RootState) => state.buyerViewPage.unreadNotificationBadge;

export default buyerViewPageSlice.reducer;
